import { useEffect, useState } from 'react';
import {
  collection,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { feedRecordsPath } from './feedRegistry';
import { matchesAllTerms, mostSelectiveTerm, queryTerms } from './searchTokens';

/**
 * The last character Firestore will order after any realistic string, which turns an ordered
 * range into a prefix match: everything from the query up to query+U+F8FF.
 */
const HIGH_CODE_POINT = '\uf8ff';

/** Long enough that a search means something, short enough to feel like type-ahead. */
const MIN_QUERY_LENGTH = 2;

/**
 * How long to wait after the last keystroke. Every search is three Firestore queries, so firing
 * on each character turned a typed word into a dozen round-trips for results nobody read.
 */
const DEBOUNCE_MS = 250;

/**
 * Type-ahead search over an imported directory.
 *
 * TWO STRATEGIES, merged, because they answer different halves of the question.
 *
 * `searchTokens` (`array-contains`) is the one that matters: the import stores every word prefix
 * of every searchable column, so "smith" finds `JOHN SMITH HOLDINGS` and "5747" finds `005747`.
 * Firestore allows only ONE array-contains clause per query, so a multi-word search queries on
 * its most selective term and the remaining terms are applied to the results here — the AND that
 * the database cannot express. See lib/searchTokens.js.
 *
 * The two PREFIX queries on `clientNumber` and `clientNameLower` are kept as well, and are not
 * redundant: records imported before tokens existed have no `searchTokens` array and would
 * otherwise become unfindable, and a prefix range still matches a term longer than the stored
 * token length. Keeping both means an upgrade needs no migration to stay usable.
 *
 * Inactive records are excluded unless the firm has turned that on; both query shapes are
 * indexed in firestore.indexes.json, since an equality filter beside a range or an
 * array-contains needs a composite index.
 *
 * Deliberately getDocs, not onSnapshot: a search result is a question asked once, not a live
 * view, and a listener per keystroke would leak subscriptions.
 */
export function useClientSearch(term, { max = 8, includeInactive = false, feedId = 'clients' } = {}) {
  const [state, setState] = useState({ results: [], searching: false, error: null });

  useEffect(() => {
    const needle = String(term || '').trim();
    if (needle.length < MIN_QUERY_LENGTH) {
      setState({ results: [], searching: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, searching: true, error: null }));

    const timer = setTimeout(() => {
      const path = feedRecordsPath(feedId);
      const activeOnly = includeInactive ? [] : [where('isActive', '==', true)];
      const records = () => collection(db, ...path);

      const prefixQuery = (field, value) =>
        getDocs(
          query(
            records(),
            // Ordering by the searched field is what makes startAt/endAt a prefix match; the
            // equality filter has to come with it, hence the composite indexes.
            ...activeOnly,
            orderBy(field),
            startAt(value),
            endAt(value + HIGH_CODE_POINT),
            limit(max)
          )
        );

      const terms = queryTerms(needle);
      const tokenTerm = mostSelectiveTerm(terms);
      const tokenQuery = tokenTerm
        ? getDocs(
            query(
              records(),
              ...activeOnly,
              where('searchTokens', 'array-contains', tokenTerm),
              // Over-fetched on purpose: the remaining terms are applied below, so the page of
              // candidates has to be bigger than the page of answers.
              limit(max * 5)
            )
          )
        : Promise.resolve({ docs: [] });

      Promise.all([
        tokenQuery,
        prefixQuery('clientNumber', needle),
        prefixQuery('clientNameLower', needle.toLowerCase()),
      ])
        .then(([byToken, byNumber, byName]) => {
          if (cancelled) return;
          const byId = new Map();

          // Token hits first: they are the ones that matched a word anywhere, and they are
          // filtered to satisfy EVERY term rather than just the one Firestore could index.
          for (const snap of byToken.docs) {
            const row = { id: snap.id, ...snap.data() };
            if (matchesAllTerms(row.searchTokens, terms)) byId.set(snap.id, row);
          }
          // Then the prefix hits, which cover records with no tokens at all.
          for (const snap of [...byNumber.docs, ...byName.docs]) {
            if (!byId.has(snap.id)) byId.set(snap.id, { id: snap.id, ...snap.data() });
          }

          setState({ results: [...byId.values()].slice(0, max), searching: false, error: null });
        })
        .catch((error) => {
          if (!cancelled) setState({ results: [], searching: false, error });
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, max, includeInactive, feedId]);

  return state;
}
